/**
 * Catches accounts that predate the consent question.
 *
 * Everyone who signed up before this feature existed has `ai_consent === null`
 * — never asked. Their answers are already being withheld from the AI marker
 * server-side, and /api/dialog/start refuses to open a lesson until they
 * answer, so without this banner they would simply find themselves locked out
 * with no way to see why.
 *
 * Answerable in place. Sending someone to a settings page to unblock the thing
 * they just tried to do is how a consent prompt turns into an obstacle nobody
 * reads, and the answer given to get past it is worth nothing.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { ApiError, apiPost } from "../api/client";
import type { User } from "../types";

export function AiConsentPrompt() {
  const { user, applyUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // `=== null` and not falsiness: `false` is a real answer — someone who
  // declined must not be asked again on every page they visit.
  if (!user || user.ai_consent !== null) return null;

  async function answer(granted: boolean) {
    setError("");
    setSaving(true);
    try {
      applyUser(await apiPost<User>("/api/auth/consent/ai", { granted }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save that.");
      setSaving(false);
    }
    // No setSaving(false) on success — the banner unmounts.
  }

  return (
    <div className="consent-banner" role="region" aria-label="AI marking consent">
      <div className="consent-banner-text">
        <strong>One question before you carry on.</strong> PyDialog can mark
        your written answers with an AI marker, which means sending what you
        type — but not your name or contact details — to an external service.
        May we? <Link to="/account#ai-consent">What this means</Link>
      </div>
      <div className="consent-banner-actions">
        <button
          className="btn btn-primary btn-sm"
          onClick={() => answer(true)}
          disabled={saving}
        >
          Yes, use AI marking
        </button>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => answer(false)}
          disabled={saving}
        >
          No, keep it on the server
        </button>
      </div>
      {error && <span className="consent-banner-error" role="alert">{error}</span>}
    </div>
  );
}
